/**
 * Payline Token Storage & Card Helpers
 * Utilities for secure token persistence and card data management
 */

const CARD_STORAGE_KEY = 'payline_card_';

/**
 * Store Payline token securely
 * @param {string} token - Payline token/subscriber ID
 * @param {string} accountId - Account identifier for keying
 * @param {object} metadata - Card metadata (brand, maskedNumber, expiryDate, etc.)
 */
export const storeToken = (token, accountId, metadata = {}) => {
  if (!token || !accountId) {
    throw new Error('Token and accountId are required for storage');
  }

  const key = `${CARD_STORAGE_KEY}${accountId}`;
  const data = {
    token,
    metadata,
    storedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to store token:', error);
    return false;
  }
};

/**
 * Retrieve stored Payline token
 * @param {string} accountId - Account identifier
 * @returns {object|null} - { token, metadata, storedAt } or null if not found
 */
export const getStoredToken = (accountId) => {
  if (!accountId) return null;

  const key = `${CARD_STORAGE_KEY}${accountId}`;
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to retrieve token:', error);
    return null;
  }
};

/**
 * Delete stored Payline token
 * @param {string} accountId - Account identifier
 * @returns {boolean} - Success status
 */
export const deleteStoredToken = (accountId) => {
  if (!accountId) return false;

  const key = `${CARD_STORAGE_KEY}${accountId}`;
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to delete token:', error);
    return false;
  }
};

/**
 * List all stored Payline tokens
 * @returns {array} - Array of { accountId, token, metadata, storedAt }
 */
export const listStoredTokens = () => {
  try {
    const tokens = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CARD_STORAGE_KEY)) {
        const accountId = key.replace(CARD_STORAGE_KEY, '');
        const data = JSON.parse(localStorage.getItem(key));
        tokens.push({ accountId, ...data });
      }
    }
    return tokens;
  } catch (error) {
    console.error('Failed to list tokens:', error);
    return [];
  }
};

/**
 * Format card data for Payline API submission
 * @param {object} card - { number, expiry, cvv, owner }
 * @returns {object} - Formatted card object for API
 */
export const formatCardForPayline = (card) => {
  if (!card || !card.number) {
    throw new Error('Card number is required');
  }

  return {
    number: card.number.replace(/\s/g, ''),
    expiryDate: (card.expiry && card.expiry.replace(/\s/g, '')) || '',
    cvv: card.cvv,
    cardholder: card.owner,
  };
};

/**
 * Extract card metadata from Payline response
 * @param {object} response - Payline API response
 * @returns {object} - { brand, maskedNumber, expiryDate, countryCode }
 */
export const extractCardMetadata = (response) => ({
  brand: (response && (response.cardBrand || response.cardType)) || '',
  maskedNumber: response && response.maskedCardNumber,
  expiryDate: response && response.expiryDate,
  countryCode: response && response.cardCountry,
  walletId: response && response.walletId,
  token: response && response.token,
});

/**
 * Convert card metadata to display format
 * @param {object} metadata - Card metadata from storage/API
 * @returns {object} - { displayNumber, brand, expiryDisplay }
 */
export const formatCardForDisplay = (metadata) => {
  const maskedNumber = (metadata && metadata.maskedNumber) || '****';
  const brand = (metadata && metadata.brand) || 'Unknown';
  let expiryDisplay = 'N/A';
  if (metadata && metadata.expiryDate) {
    expiryDisplay = `${metadata.expiryDate.substring(2, 4)}/${metadata.expiryDate.substring(0, 2)}`;
  }

  return {
    displayNumber: maskedNumber,
    brand,
    expiryDisplay,
  };
};
